import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Input, Button, logoDark, logoLight } from '@react-apps/design-system';
import { authContainer } from '../../di/container';

function apiCode(err: unknown): string | undefined {
  return typeof err === 'object' && err !== null && 'response' in err
    ? (err as { response?: { data?: { error?: string } } }).response?.data
        ?.error
    : undefined;
}

/**
 * Ato 2 da troca de e-mail (rota pública): a pessoa abriu o link do e-mail atual
 * (token START na query) e informa o **e-mail novo**. Ao enviar, o back manda um
 * link de confirmação para o novo endereço.
 */
export function SubmitNewEmailPage() {
  const [searchParams] = useSearchParams();
  /**
   * O token vem na query do link do e-mail. Ele é capturado uma vez no
   * primeiro render e some da barra de endereços em seguida: enquanto está lá,
   * entra no histórico do navegador e no `Referer` de qualquer recurso externo
   * carregado pela página. Mesma mitigação do `PasswordResetCard`.
   */
  const [token] = useState(() => searchParams.get('token'));

  useEffect(() => {
    if (window.location.search) {
      window.history.replaceState(null, '', window.location.pathname);
    }
  }, []);

  const [newEmail, setNewEmail] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [formError, setFormError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [isDark, setIsDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return document.documentElement.classList.contains('dark');
  });
  useEffect(() => {
    const observer = new MutationObserver(() =>
      setIsDark(document.documentElement.classList.contains('dark')),
    );
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });
    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (!token) return;
    if (newEmail !== confirmEmail) {
      setFormError('Os e-mails não coincidem.');
      return;
    }
    setFormError(null);
    setIsLoading(true);
    try {
      await authContainer.repository.submitEmailChange({ token, newEmail });
      setDone(true);
    } catch (err) {
      const code = apiCode(err);
      if (code === 'EMAIL_ALREADY_IN_USE') {
        setFormError(
          'Já existe uma conta com esse e-mail. Procure o suporte para resolver.',
        );
      } else if (
        code === 'ACTION_TOKEN_NOT_FOUND' ||
        code === 'TOKEN_EXPIRED' ||
        code === 'TOKEN_ALREADY_USED'
      ) {
        setFormError(
          'Este link é inválido ou já expirou. Peça a troca de novo.',
        );
      } else if (code === 'EMAIL_UNCHANGED') {
        setFormError('O novo e-mail é igual ao atual.');
      } else {
        setFormError('Não foi possível continuar. Tente novamente.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-(--color-bg-secondary) flex min-h-screen flex-col items-center justify-center px-4">
      <div className="flex w-full max-w-sm flex-1 flex-col items-center justify-center">
        <div className="mb-8">
          <img
            src={isDark ? logoDark : logoLight}
            alt="Primum"
            className="h-10 w-auto"
          />
        </div>

        <div className="border-(--color-border) bg-(--color-bg-primary) w-full rounded-2xl border p-8 shadow-sm">
          {!token ? (
            <ErrorBlock message="Link inválido ou expirado. Peça a troca de novo." />
          ) : done ? (
            <div className="space-y-6">
              <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-6 text-center dark:border-green-900 dark:bg-green-950">
                <p className="text-sm font-semibold text-green-700 dark:text-green-400">
                  Quase lá!
                </p>
                <p className="mt-2 text-sm text-green-700 dark:text-green-400">
                  Enviamos um link de confirmação para{' '}
                  <strong>{newEmail}</strong>. Abra-o para concluir a troca.
                </p>
              </div>
            </div>
          ) : (
            <form onSubmit={(e) => void handleSubmit(e)} className="space-y-5">
              <div className="mb-2 text-center">
                <h1 className="text-(--color-text-primary) text-xl font-bold">
                  Informe o novo e-mail
                </h1>
              </div>
              <Input
                id="newEmail"
                label="Novo e-mail"
                type="email"
                required
                autoComplete="email"
                value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)}
                placeholder="novo@email.com"
              />
              <Input
                id="confirmEmail"
                label="Confirmar novo e-mail"
                type="email"
                required
                autoComplete="email"
                value={confirmEmail}
                onChange={(e) => setConfirmEmail(e.target.value)}
                placeholder="novo@email.com"
              />
              {formError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400">
                  {formError}
                </div>
              )}
              <Button
                type="submit"
                disabled={isLoading || !newEmail || !confirmEmail}
                className="w-full"
              >
                {isLoading ? 'Enviando…' : 'Enviar link de confirmação'}
              </Button>
            </form>
          )}
        </div>
      </div>

      <footer className="py-6">
        <p className="text-(--color-text-secondary) text-xs">
          © 2026 Grupo Primum. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  );
}

function ErrorBlock({ message }: { message: string }) {
  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-6 text-center dark:border-red-900 dark:bg-red-950">
        <p className="text-sm font-medium text-red-700 dark:text-red-400">
          {message}
        </p>
      </div>
      <Link
        to="/sign-in"
        className="bg-(--color-accent) flex w-full items-center justify-center rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
      >
        Voltar para o login
      </Link>
    </div>
  );
}
